import { CARE_GROUP_PREFIX_PATH, PUBLIC_HOST, USES_SUBDOMAIN } from "configs/AppConfig"

interface PracticePathOptions {
  /** Path within the practice site, e.g. `contact` or a page slug. Defaults to its home page. */
  directory?: string

  /** Return the whole address — scheme and host included — rather than a path within the current site. */
  fullUrl?: boolean
}

export const practicePathForSlug = (slug: string, { directory = "", fullUrl = false }: PracticePathOptions = {}) => {
  const path = USES_SUBDOMAIN ? `/${directory}` : `/${slug ? `${slug}/` : ""}${directory}`

  if (fullUrl) {
    let host = window.location.host

    if (slug && USES_SUBDOMAIN) {
      host = `${slug}.${PUBLIC_HOST}`
    }

    return `${window.location.protocol}//${host}${path}`
  }

  return path
}

/** The fixed parts of a web address, around the slug someone types. */
export interface WebAddressAffixes {
  prefix: string
  suffix: string
}

/**
 * Stands in for the slug while the fixed parts of an address are derived. It is
 * itself a valid slug, so it survives the encoding a real slug goes through.
 */
const SLUG_PLACEHOLDER = "slugplaceholder"

/**
 * Splits the address a slug ends up in into the parts around it, so a slug input
 * can show them next to the box. Derived from the same builder that produces the
 * address itself, so the two can never disagree: a deployment without practice
 * subdomains puts the slug in the path, and then the whole host is the prefix.
 */
export const webAddressAffixes = (buildAddress: (slug: string) => string): WebAddressAffixes => {
  const address = buildAddress(SLUG_PLACEHOLDER)
  const slugStart = address.indexOf(SLUG_PLACEHOLDER)

  // A builder that drops the slug leaves nothing to put around the box, and one
  // that repeats it offers no single point to split the address on.
  if (slugStart < 0 || address.includes(SLUG_PLACEHOLDER, slugStart + SLUG_PLACEHOLDER.length)) {
    return { prefix: "", suffix: "" }
  }

  return {
    prefix: address.slice(0, slugStart),
    // The address ends in the slash that separates it from a page path; that is
    // part of the address, not of the host shown behind the box.
    suffix: address.slice(slugStart + SLUG_PLACEHOLDER.length).replace(/\/$/, ""),
  }
}

export const careGroupPathForSlug = (slug: string, directory = "") => {
  // NOTE: Uses `CARE_GROUP_PREFIX_PATH` (even in production) to handle
  // conflicting routes with practice slugs
  const domain = "zorggroep"
  const host = USES_SUBDOMAIN ? `${domain}.${PUBLIC_HOST}` : window.location.host
  const path = `${CARE_GROUP_PREFIX_PATH}/${slug}`
  const nested = directory ? `/${directory}` : ""

  return `${window.location.protocol}//${host}${path}${nested}`
}
